import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import type { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import PaginationFooter from 'components/common/PaginationFooter';
import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import type { Client } from 'services/types/client';
import { NAVY, SHADOW } from 'styles/tokens';

import ClientListItem from './ClientListItem';

const FONT = 'Jost, sans-serif';

interface ClientListProps {
  clients: Client[];
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  sortModel: GridSortModel;
  onSortModelChange: (model: GridSortModel) => void;
  rowCount: number;
  navigateToClientId: (id: number, newPage?: boolean) => void;
  highlightedClientId: string;
  businessUnitType: string;
  isAdmin: boolean;
  isLoading?: boolean;
}

const ClientList: FC<ClientListProps> = ({
  clients,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  rowCount,
  navigateToClientId,
  highlightedClientId,
  businessUnitType,
  isAdmin,
  isLoading = false,
}) => {
  const highlightedRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (highlightedRef.current) {
      highlightedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [clients, highlightedClientId]);

  const handleClick = (id: number, event: React.MouseEvent) => {
    navigateToClientId(id, event.ctrlKey || event.metaKey);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      {/* Scrollable list */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, overflowY: 'auto', flex: 1 }}>
        {clients.map((client) => {
          const isHighlighted = client.id.toString() === highlightedClientId;
          return (
            <div key={client.id} ref={isHighlighted ? highlightedRef : undefined}>
              <ClientListItem
                client={client}
                isHighlighted={isHighlighted}
                businessUnitType={businessUnitType}
                isAdmin={isAdmin}
                onClick={handleClick}
              />
            </div>
          );
        })}

        {isLoading && clients.length === 0 && (
          <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress sx={{ color: NAVY }} />
          </Box>
        )}

        {!isLoading && clients.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography sx={{ color: `${NAVY}80`, fontFamily: FONT, fontSize: '0.9rem' }}>No hay resultados</Typography>
          </Box>
        )}
      </Box>

      {isLoading && clients.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.5,
            bgcolor: 'rgba(249, 250, 253, 0.85)',
            backdropFilter: 'blur(12px)',
            borderRadius: '999px',
            boxShadow: SHADOW,
            pointerEvents: 'none',
          }}
        >
          <CircularProgress size={14} sx={{ color: NAVY }} />
          <Typography sx={{ color: NAVY, fontSize: '0.75rem', fontFamily: FONT }}>Cargando…</Typography>
        </Box>
      )}

      <PaginationFooter
        page={paginationModel.page}
        pageSize={paginationModel.pageSize}
        rowCount={rowCount}
        onPageChange={(page) => onPaginationModelChange({ ...paginationModel, page })}
        onPageSizeChange={(pageSize) => onPaginationModelChange({ ...paginationModel, pageSize, page: 0 })}
      />
    </Box>
  );
};

export default ClientList;
